import { useEffect, useRef, useState } from "react";
import { sharedAttributionVisitorId, useAttributionVisitor } from "../../shared/attribution/useAttributionVisitor";

export const AVAILABLE = "available";
export const SOLD_OUT = "sold_out";
export const ARCHIVED = "archived";
export const NOT_FOR_SALE = "not_for_sale";

export const CODE_AMOUNT_OUT_OF_RANGE = "amount_out_of_range";
export const CODE_OVER_PER_ORDER_LIMIT = "over_per_order_limit";
export const CODE_PRICE_CHANGED = "price_changed";
export const CODE_BONUS_CHANGED = "bonus_changed";
const QUOTE_REFRESH_CODES = [CODE_PRICE_CHANGED, CODE_BONUS_CHANGED];
export const CODE_DECLINED = "declined";
export const CODE_RATE_LIMITED = "rate_limited";
export const CODE_DELIVERY_DATE_INVALID = "delivery_date_invalid";

export const SEND_NOW = "now";
export const SEND_LATER = "later";

const UNAVAILABLE_CODES = [SOLD_OUT, ARCHIVED, NOT_FOR_SALE];
const QUOTE_DELAY_MS = 300;
const ORDER_TOKEN_PARAM = "order_token";
const PROCESSING = "processing";
const SUCCEEDED = "succeeded";
const RATE_LIMITED_MESSAGE = "Too many attempts. Please wait a minute and try again.";
const NETWORK_MESSAGE = "We couldn't reach Opendate. Check your connection and try again.";
const TURNSTILE_MESSAGE = "We couldn't verify you're human. Please try again.";

const emailValid = (value) => /\S+@\S+\.\S+/.test(value.trim());

const csrfToken = () => {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute("content") : null;
};

const dollarsToCents = (dollars) => {
  const amount = Number.parseFloat(dollars);
  return Number.isFinite(amount) && amount > 0 ? Math.round(amount * 100) : null;
};

const redirectParams = () => {
  const params = new URLSearchParams(window.location.search);
  return { status: params.get("redirect_status"), orderToken: params.get(ORDER_TOKEN_PARAM) };
};

const returnUrl = (orderToken) => `${window.location.href.split("?")[0]}?${ORDER_TOKEN_PARAM}=${encodeURIComponent(orderToken)}`;

const confirmationFromOrder = (order, processing) => ({
  orderNumber: order.order_number,
  amountCents: order.amount_cents,
  bonusCents: order.bonus_cents || null,
  quantity: order.quantity,
  totalCents: order.total_cents,
  recipientName: order.recipient_name,
  recipientEmail: order.recipient_email,
  deliveryLabel: order.delivery_label || null,
  email: order.email,
  processing,
});

const responseError = (status, data) => {
  if (status === 429) return { code: CODE_RATE_LIMITED, message: RATE_LIMITED_MESSAGE };

  return {
    code: data.code || null,
    message: (data.errors || ["Something went wrong. Please try again."]).join(" "),
  };
};

const pad = (value) => String(value).padStart(2, "0");

export const isoDate = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const parseIsoDate = (value) => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const LONG_DATE = { month: "long", day: "numeric", year: "numeric" };

const deliveryLabelFor = (date, delivery) => `${date.toLocaleDateString("en-US", LONG_DATE)} at ${delivery.timeLabel}`;

const firstAmountCents = (product) => product.denominationsCents[0] || product.customMinCents || null;

export default function useGiftCardPurchase({ product, availability, initialQuote, quoteUrl, ordersUrl, delivery }) {
  const attributionVisitorUuid = useAttributionVisitor("od_embed.js");
  const [currentAvailability, setCurrentAvailability] = useState(availability);
  const [selectedAmountCents, setSelectedAmountCents] = useState(firstAmountCents(product));
  const [customAmount, setCustomAmount] = useState("");
  const [isCustomAmount, setIsCustomAmount] = useState(product.denominationsCents.length === 0);
  const [quantity, setQuantity] = useState(1);
  const [isGift, setIsGift] = useState(true);
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sendWhen, setSendWhen] = useState(SEND_NOW);
  const [deliverOn, setDeliverOn] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [quote, setQuote] = useState(initialQuote);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const [showFieldErrors, setShowFieldErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [quoteRefresh, setQuoteRefresh] = useState(0);
  const latestQuoteRequest = useRef(0);
  const placedOrder = useRef(null);

  const amountCents = isCustomAmount ? dollarsToCents(customAmount) : selectedAmountCents;
  const quoteIsCurrent = Boolean(quote) && quote.amount_cents === amountCents && quote.quantity === quantity;

  useEffect(() => {
    const { status, orderToken } = redirectParams();
    if (!status || !orderToken) return;

    placedOrder.current = { token: orderToken };
    if (status !== SUCCEEDED && status !== PROCESSING) {
      setError({ code: CODE_DECLINED, message: "Your card was declined. Try another card." });
      return;
    }

    fetch(`${ordersUrl}/${encodeURIComponent(orderToken)}`, { headers: { Accept: "application/json" } })
      .then((response) => (response.ok ? response.json() : Promise.reject(response)))
      .then((order) => setConfirmation(confirmationFromOrder(order, status === PROCESSING)))
      .catch(() => setConfirmation({ processing: status === PROCESSING }));
  }, []);

  useEffect(() => {
    if (currentAvailability !== AVAILABLE) return undefined;
    if (amountCents === null) {
      setQuoteError(null);
      setQuoteLoading(false);
      return undefined;
    }
    if (quoteIsCurrent && quoteRefresh === 0) return undefined;

    const requestId = latestQuoteRequest.current + 1;
    latestQuoteRequest.current = requestId;
    setQuoteLoading(true);

    const timer = window.setTimeout(async () => {
      try {
        const url = `${quoteUrl}?amount_cents=${amountCents}&quantity=${quantity}`;
        const response = await fetch(url, { headers: { Accept: "application/json" } });
        const data = await response.json().catch(() => ({}));
        if (requestId !== latestQuoteRequest.current) return;

        if (response.ok) {
          setQuote({ ...data, amount_cents: amountCents, quantity });
          setQuoteError(null);
        } else if (UNAVAILABLE_CODES.includes(data.code)) {
          setCurrentAvailability(data.code);
        } else {
          setQuoteError(responseError(response.status, data));
        }
      } catch (fetchError) {
        if (requestId === latestQuoteRequest.current) setQuoteError({ code: null, message: NETWORK_MESSAGE });
      }
      if (requestId === latestQuoteRequest.current) setQuoteLoading(false);
    }, QUOTE_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [amountCents, quantity, quoteRefresh, currentAvailability]);

  const fieldErrors = {
    customAmount: isCustomAmount && amountCents === null ? "Enter an amount" : null,
    recipientName: isGift && !recipientName.trim() ? "Enter their name" : null,
    recipientEmail: isGift && !emailValid(recipientEmail) ? "Enter a valid email" : null,
    deliverOn: isGift && sendWhen === SEND_LATER && !deliverOn ? "Pick a delivery date" : null,
    firstName: !firstName.trim() ? "Enter your first name" : null,
    lastName: !lastName.trim() ? "Enter your last name" : null,
    email: !emailValid(email) ? "Enter a valid email" : null,
    agreed: !agreed ? "Agree to the terms to continue" : null,
  };
  const formComplete = Object.values(fieldErrors).every((fieldError) => fieldError === null);
  const visibleFieldErrors = showFieldErrors ? fieldErrors : {};

  const chooseAmount = (value) => {
    setError(null);
    if (value === "custom") {
      setIsCustomAmount(true);
      return;
    }
    setIsCustomAmount(false);
    setSelectedAmountCents(value);
  };

  const changeQuantity = (next) => {
    setError(null);
    setQuantity(Math.min(Math.max(next, 1), product.maxPerOrder));
  };

  const chooseDeliverOn = (date) => {
    setError(null);
    setDeliverOn(date);
  };

  const scheduled = isGift && sendWhen === SEND_LATER && Boolean(deliverOn);

  const orderParams = () => ({
    amount_cents: amountCents,
    quantity,
    expected_total: quote.total,
    expected_bonus_cents: quote.bonus_cents ?? "",
    first_name: firstName.trim(),
    last_name: lastName.trim(),
    email: email.trim(),
    recipient_name: isGift ? recipientName.trim() : "",
    recipient_email: isGift ? recipientEmail.trim() : "",
    message: isGift ? message.trim() : "",
    deliver_on: scheduled ? isoDate(deliverOn) : "",
  });

  const placeOrder = async (recaptchaToken) => {
    const params = {
      ...orderParams(),
      ...(placedOrder.current ? { order_token: placedOrder.current.token } : {}),
    };
    const token = csrfToken();
    const visitorUuid = attributionVisitorUuid || sharedAttributionVisitorId();
    const response = await fetch(ordersUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(token ? { "X-CSRF-Token": token } : {}),
      },
      body: JSON.stringify({
        gift_card_order: params,
        recaptcha_token: recaptchaToken,
        attribution_visitor_uuid: visitorUuid || "",
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw responseError(response.status, data);

    placedOrder.current = {
      token: data.token,
      clientSecret: data.stripe_client_secret,
      orderNumber: data.order_number,
      bonusCents: data.bonus_cents || null,
    };
    return placedOrder.current;
  };

  const handleRefusal = (refusal) => {
    if (UNAVAILABLE_CODES.includes(refusal.code)) {
      setCurrentAvailability(refusal.code);
    } else if (QUOTE_REFRESH_CODES.includes(refusal.code)) {
      setQuoteRefresh((count) => count + 1);
    }
    setError(refusal);
  };

  const pay = async ({ stripe, elements, turnstileRef }) => {
    if (submitting || !stripe || !elements) return;
    if (!formComplete || !quoteIsCurrent) {
      setShowFieldErrors(true);
      return;
    }

    setSubmitting(true);
    setError(null);

    const { error: elementsError } = await elements.submit();
    if (elementsError) {
      setSubmitting(false);
      return;
    }

    let recaptchaToken;
    try {
      recaptchaToken = await turnstileRef.current.executeAsync();
    } catch (turnstileError) {
      turnstileRef.current.reset();
      setError({ code: null, message: TURNSTILE_MESSAGE });
      setSubmitting(false);
      return;
    }

    let order;
    try {
      order = await placeOrder(recaptchaToken);
    } catch (refusal) {
      handleRefusal("code" in refusal ? refusal : { code: null, message: NETWORK_MESSAGE });
      setSubmitting(false);
      return;
    }

    const { error: paymentError, paymentIntent } = await stripe.confirmPayment({
      elements,
      clientSecret: order.clientSecret,
      confirmParams: { return_url: returnUrl(order.token) },
      redirect: "if_required",
    });

    if (paymentError) {
      setError({ code: CODE_DECLINED, message: paymentError.message });
      setSubmitting(false);
      return;
    }

    setConfirmation({
      orderNumber: order.orderNumber,
      amountCents,
      bonusCents: order.bonusCents,
      quantity,
      totalCents: quote.total_cents,
      recipientName: isGift ? recipientName.trim() : null,
      recipientEmail: isGift ? recipientEmail.trim() : null,
      deliveryLabel: scheduled ? deliveryLabelFor(deliverOn, delivery) : null,
      email: email.trim(),
      processing: Boolean(paymentIntent) && paymentIntent.status === PROCESSING,
    });
    setSubmitting(false);
  };

  const buyAnother = () => {
    placedOrder.current = null;
    setConfirmation(null);
    setError(null);
    setShowFieldErrors(false);
    setAgreed(false);
    setQuantity(1);
    setRecipientName("");
    setRecipientEmail("");
    setMessage("");
    setSendWhen(SEND_NOW);
    setDeliverOn(null);
    window.history.replaceState(null, "", window.location.pathname);
  };

  return {
    availability: currentAvailability,
    amountCents,
    selectedAmountCents,
    isCustomAmount,
    customAmount,
    setCustomAmount,
    chooseAmount,
    quantity,
    changeQuantity,
    isGift,
    setIsGift,
    recipientName,
    setRecipientName,
    recipientEmail,
    setRecipientEmail,
    message,
    setMessage,
    sendWhen,
    setSendWhen,
    deliverOn,
    setDeliverOn: chooseDeliverOn,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    email,
    setEmail,
    agreed,
    setAgreed,
    quote: quoteIsCurrent ? quote : null,
    quoteLoading,
    quoteError,
    fieldErrors: visibleFieldErrors,
    submitting,
    error,
    confirmation,
    pay,
    buyAnother,
  };
}
